import React from "react";
import { ApiKey } from "../types/api";
import { Button } from "@/components/ui/button";
import { TableCell, TableRow } from "@/components/ui/table";
import { Trash2, Calendar } from "lucide-react";

interface ApiKeyRowProps {
  apiKey: ApiKey;
  onDelete: (id: string) => Promise<boolean>;
  isFirst?: boolean;
}

const formatMaskedKey = (apiKeyMasked: string) => {
  if (apiKeyMasked.length <= 12) return apiKeyMasked;
  return `${apiKeyMasked.slice(0, 12)}${apiKeyMasked.slice(-12)}`;
};

export const ApiKeyRow: React.FC<ApiKeyRowProps> = ({
  apiKey,
  onDelete,
  isFirst = false,
}) => {
  const formatDate = (date: Date) => {
    const now = new Date();
    const createdDate = new Date(date);
    const diffInDays = Math.floor(
      (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24),
    );

    if (diffInDays === 0) {
      return "Today";
    } else if (diffInDays === 1) {
      return "Yesterday";
    } else if (diffInDays < 7) {
      return `${diffInDays} days ago`;
    } else {
      return createdDate.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
  };

  const formatFullDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <TableRow className="group hover:bg-slate-50/50 dark:hover:bg-slate-800/50 transition-colors border-slate-200/60 dark:border-slate-700/60">
      <TableCell className="w-1/4 min-w-[150px] py-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex flex-col truncate">
            <span className="font-semibold text-slate-900 dark:text-slate-100 text-sm sm:text-base truncate">
              {apiKey.name}
            </span>
            {isFirst && (
              <span className="text-xs text-blue-600 dark:text-blue-400 font-medium">
                Most recent
              </span>
            )}
          </div>
        </div>
      </TableCell>

      <TableCell className="w-1/3 min-w-[200px] py-4">
        <div className="flex items-center gap-3 min-w-0">
          <code className="relative rounded-md bg-slate-100 dark:bg-slate-800/70 px-3 py-2 font-mono text-xs sm:text-sm border border-slate-200/60 dark:border-slate-700/60 text-slate-700 dark:text-slate-300 truncate">
            {formatMaskedKey(apiKey.apiKeyMasked)}
          </code>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="text-xs text-slate-500 dark:text-slate-400">
              Hidden for security
            </div>
          </div>
        </div>
      </TableCell>

      <TableCell className="w-1/4 min-w-[180px] py-4">
        <div className="flex items-center gap-2 min-w-0">
          <Calendar className="h-4 w-4 text-slate-400 dark:text-slate-500 shrink-0" />
          <div className="flex flex-col min-w-0">
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
              {formatDate(apiKey.createdAt)}
            </span>
            <span
              className="text-xs text-slate-500 dark:text-slate-400 truncate"
              title={formatFullDate(apiKey.createdAt)}
            >
              {formatFullDate(apiKey.createdAt)}
            </span>
          </div>
        </div>
      </TableCell>

      <TableCell className="w-[100px] min-w-[80px] py-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(apiKey.id)}
          className="h-9 w-9 p-0 text-slate-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors"
          title={`Delete "${apiKey.name}" API key`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
};
